import { Callout } from '@radix-ui/themes';
import { use, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { resetAnalyticsKeepingConsent } from '../../../analytics/consent';
import { setAccessToken, tryManagerRefreshToken } from '../../accesstoken';
import { config } from '../../../config';
import Title from '../../elements/Title';
import LocaleContext from '../../hooks/LocaleContext';
import { MessageListener } from '../../MessageListener';
import { setExpectedUserId } from '../../sessionIdentity';

const managerOrigin = new URL(config.VITE_MANAGER_URL).origin;
const GRANT_TIMEOUT_MS = 10_000;

const AdminLogin = () => {
  const { t } = useTranslation();
  const { setLoggedIn } = use(LocaleContext);
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const handledRef = useRef(false);

  const onMessage = useCallback(
    (e: MessageEvent) => {
      if (e.origin !== managerOrigin || e.data?.type !== 'impersonation-grant' || handledRef.current) {
        return;
      }
      handledRef.current = true;

      fetch(`${config.VITE_GRAPHQL_URL}/api/users/impersonate`, {
        method: 'POST',
        credentials: 'include',
        body: JSON.stringify({ grant: e.data.grant }),
        headers: { 'content-type': 'application/json' },
      })
        .then((r) => {
          if (!r.ok) throw new Error('Invalid grant');
          return r.json();
        })
        .then(async (data) => {
          resetAnalyticsKeepingConsent();
          setAccessToken(data.accessToken);
          setExpectedUserId(data.accessToken);
          await tryManagerRefreshToken(data.refreshToken);
          setLoggedIn?.(true);
          navigate('/');
        })
        .catch(() => setError(t('adminLogin.error.generic')));
    },
    [navigate, setLoggedIn, t],
  );

  useEffect(() => {
    window.opener?.postMessage({ type: 'admin-login-ready' }, managerOrigin);

    const timeout = setTimeout(() => {
      if (!handledRef.current) {
        setError(t('adminLogin.error.timeout'));
      }
    }, GRANT_TIMEOUT_MS);

    return () => clearTimeout(timeout);
  }, [t]);

  return (
    <MessageListener onMessage={onMessage}>
      <div className="vh-100 flex items-center justify-center">
        <Title title={t('adminLogin.pageTitle')} />
        <div className="pb-12 w-3/4 text-center" style={{ maxWidth: '25rem' }}>
          <img src="/logo/hubert_logo_lockup_dark@2x.png" className="mw-100" alt={t('setPassword.logo.alt')} />
          {error ? (
            <>
              <Callout.Root color="red" className="mt-4">
                <Callout.Text className="mb-0">{error}</Callout.Text>
              </Callout.Root>
              <a href="/signin" className="mt-4 inline-block">
                {t('adminLogin.backToSignIn')}
              </a>
            </>
          ) : (
            <h2 style={{ fontSize: '1.9rem' }} className="mt-4">
              {t('adminLogin.loading')}
            </h2>
          )}
        </div>
      </div>
    </MessageListener>
  );
};

export default AdminLogin;
